import React, { memo, useEffect, useMemo } from 'react';
import { DatePicker, Form, Radio, TimePicker } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import './sticky-countdown.scss';
import { ColorSelector } from './colorSelector';
import { DurationInput } from './durationInput';
import { isEqual } from 'lodash';
import { TimerCard } from './timerCard';

dayjs.extend(customParseFormat);

function getSeconds(duration) {
  if (duration === null) {
    duration = { hour: 0, minute: 0 };
  }

  const { hour, minute } = duration;

  const seconds = hour * 3600 + minute * 60;

  return seconds;
}

function getDuration(seconds) {
  if (seconds === null || typeof seconds === 'undefined') seconds = 0;

  seconds = seconds / 60;

  const minute = Math.floor(seconds % 60);

  const hour = Math.floor(seconds / 60);

  return { hour, minute };
}

function disabledDates(current) {
  return current && current < dayjs().startOf('day');
}

const disabledTime = () => {
  return {
    disabledHours: () => {
      const hours = [];
      const currentHour = dayjs().hour();

      for (let i = currentHour - 1; i >= 0; i--) {
        hours.push(i);
      }

      return hours;
    },
    disabledMinutes: (selectedHour) => {
      const minutes = [];
      const currMinute = dayjs().minute();

      if (selectedHour === dayjs().hour()) {
        for (let i = currMinute - 1; i >= 0; i--) {
          minutes.push(i);
        }
      }

      return minutes;
    },
  };
};

function StickyCountdown(props) {
  const [form] = Form.useForm();

  const countDownType = Form.useWatch('countDownType', form);
  const enddate = Form.useWatch('enddate', form);
  const time = Form.useWatch('time', form);
  const template = Form.useWatch('type', form);

  useEffect(() => {
    if (enddate && !time) form.setFieldValue('time', dayjs());
  }, [enddate, time]);

  const initialValues = useMemo(() => {
    const { duration: seconds, endTime, ...values } = props.value;

    const duration = getDuration(seconds);

    const enddate = dayjs(endTime);
    const time = dayjs(endTime);

    return { ...values, enddate, time, duration };
  }, [props.value]);

  useEffect(() => {
    const values = form.getFieldsValue();

    if (isEqual(values, initialValues)) return;

    form.setFieldsValue(initialValues);
  }, [initialValues]);

  return (
    <Form
      form={form}
      className="sticky-countdown-content"
      initialValues={{
        ...initialValues,
      }}
      onFieldsChange={([field]) => {
        const [name] = field.name;

        if (name === 'enddate') {
          const time = form.getFieldValue('time') ?? dayjs();

          const hour = time.get('hour');
          const minute = time.get('minute');

          const endTime = dayjs(field.value)
            .set('hour', hour)
            .set('minute', minute)
            .toDate();

          return props.onChange('endTime', endTime, 'END_TIME');
        }

        if (name === 'time') {
          const date = form.getFieldValue('enddate');

          if (!date) return;

          const hour = field.value.get('hour');
          const minute = field.value.get('minute');

          const endTime = dayjs(date)
            .set('hour', hour)
            .set('minute', minute)
            .toDate();

          return props.onChange('endTime', endTime, 'END_TIME');
        }

        if (name === 'duration') {
          return props.onChange(name, getSeconds(field.value), 'DURATION');
        }

        if (name === 'type') {
          if (field.value !== 'PROGRESS_BAR') {
            form.setFieldValue('progressBarColor', null);
            props.onChange('progressBarColor', null);
          }
        }

        props.onChange(name, field.value, form.getFieldValue('countDownType'));
      }}
    >
      <Form.Item name="type" noStyle>
        <Radio.Group>
          <div className="timer-wrapper">
            <Radio value="PLAIN" className="timer-selector">
              Countdown
            </Radio>
            <Radio value="PROGRESS_BAR" className="timer-selector">
              Countdown with progress bar
            </Radio>
            <Radio value="BIG" className="timer-selector">
              Big Countdown
            </Radio>
          </div>
        </Radio.Group>
      </Form.Item>
      <div className="timer-cards">
        <Form.Item noStyle name="type">
          <TimerCard type="PLAIN">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <span style={{ fontWeight: 'bold' }}>Exclusive offer!</span>
              <span className="timer-counter">02:59:14</span>
            </div>
            <span className="timer-desc">
              Recharge Your wallet in the next 2 hours...
            </span>
          </TimerCard>
        </Form.Item>
        <Form.Item name="type" noStyle>
          <TimerCard type="PROGRESS_BAR">
            <span style={{ fontWeight: 'bold' }}>
              Use code MAX50 and get 50% off!
            </span>
            <span className="timer-desc">Hurry! Coupon expires in</span>
            <div className="timer-progress-section">
              <div className="timer-progress" />
              <span className="timer-counter">02:59:14</span>
            </div>
          </TimerCard>
        </Form.Item>
        <Form.Item name="type" noStyle>
          <TimerCard type="BIG">
            <span style={{ fontWeight: 'bold' }}>Reminder</span>
            <span className="timer-desc">
              You car insurance is expiring in 1 day. Click here to renew now!
            </span>
            <div className="big-timer-wrapper">
              <div className="big-timer">
                <div className="big-timer-part">
                  <span className="counter">24</span>
                  <span className="unit">HOURS</span>
                </div>
                <span className="time-colon">:</span>
                <div className="big-timer-part">
                  <span className="counter">59</span>
                  <span className="unit">MINUTES</span>
                </div>
                <span className="time-colon">:</span>
                <div className="big-timer-part">
                  <span className="counter">00</span>
                  <span className="unit">SECONDS</span>
                </div>
              </div>
            </div>
          </TimerCard>
        </Form.Item>
      </div>
      <div className="timer-config">
        <span className="timer-title">Set Timer</span>
        <Form.Item noStyle name="countDownType">
          <Radio.Group>
            <Radio value="DURATION">By Duration</Radio>
            <Radio value="END_TIME">By End Time</Radio>
          </Radio.Group>
        </Form.Item>
      </div>
      <div className="timer-config">
        <span className="timer-title">Timer Value</span>
        <div className="timer-values-wrapper">
          <Form.Item
            rules={[{ required: true }]}
            noStyle
            name="enddate"
            hidden={countDownType === 'DURATION'}
          >
            <DatePicker disabledDate={disabledDates} />
          </Form.Item>
          <Form.Item noStyle name="time" hidden={countDownType === 'DURATION'}>
            <TimePicker
              use12Hours
              placeholder="HH:MM"
              disabled={!enddate}
              format="h:mm a"
              disabledTime={disabledTime}
              showSecond={false}
              showNow={false}
              allowClear={false}
            />
          </Form.Item>
          <Form.Item
            rules={[{ required: countDownType === 'DURATION' }]}
            noStyle
            name="duration"
            hidden={countDownType === 'END_TIME'}
          >
            <DurationInput />
          </Form.Item>
        </div>
      </div>
      <Form.Item shouldUpdate noStyle name="timeColor">
        <ColorSelector
          shouldVisible={!!initialValues.timeColor}
          title="Timer Color"
        />
      </Form.Item>
      <Form.Item
        noStyle
        name="progressBarColor"
        hidden={template !== 'PROGRESS_BAR'}
      >
        <ColorSelector
          shouldVisible={!!initialValues.progressBarColor}
          title="Progressbar Color"
        />
      </Form.Item>
    </Form>
  );
}

const Memoized = memo(StickyCountdown);

export { Memoized as StickyCountdown };
